import { Link, useNavigate, useParams } from "react-router-dom";
import Api from "../Auth/Api";
import { useEffect, useState, useCallback, useMemo } from "react";
import { Controller, useForm } from 'react-hook-form';
import { Modal, Toast, Alert, Offcanvas } from 'react-bootstrap';
import { decodeJWT, formatTime } from '../Components/jwtUtils';
import TextareaAutosize from "react-textarea-autosize";
import Tippy from '@tippyjs/react';
import { CourseBannerSkeleton, CourseHeaderMobileSkeleton, ModuleSkeletons } from '../Components/Skeletons/ModuleSkeleton';
import AulasManager from '../Components/Dashboard/Aulas/AulasManager';
import 'tippy.js/dist/tippy.css';

function Modulos() {

    const token = JSON.parse(localStorage.getItem("user_token"))
    const nivel = decodeJWT(token).nivel;
    const navigate = useNavigate()
    const { id } = useParams()
    const [showModalEditarModulo, setShowModalEditarModulo] = useState(false)
    const [moduloEditarFile, setModuloEditarFile] = useState(false)
    const [uploadModuloProgress, setUploadModuloProgress] = useState(0)
    const [toast, setToast] = useState({})
    const [showToast, setShowToast] = useState(false)
    const [thumbEdit, setThumbEdit] = useState(false)
    const [showModalDeletarModulo, setShowModalDeletarModulo] = useState(false)
    const [moduloDeletar, setModuloDeletar] = useState([])
    const [previewUrl, setPreviewUrl] = useState(null);
    const [showDrawerMobile, setShowDrawerMobile] = useState(false)
    const [certificadoStatus, setCertificadoStatus] = useState(null);
    const [isLoadingEditarModulo, setIsLoadingEditarModulo] = useState(false);
    const [showModalDetalhesCurso, setShowModalDetalhesCurso] = useState(false);
    const isMobile = window.matchMedia('(max-width: 767px)').matches;

    // Verificar status do certificado para este curso
    useEffect(() => {
        async function verificarStatusCertificado() {
            if (nivel !== 'Aluno' && nivel !== 'Gestor') return;

            try {
                const response = await Api.get(`api/verificar-status-certificado-curso/${id}`, {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                });
                setCertificadoStatus(response.data);
            } catch (error) {
                // Erro silencioso
            }
        }

        verificarStatusCertificado();
    }, [id, token, nivel]);

    const handleKeyPress = (e) => {
        const input = e.target;
        setTimeout(() => {
            input.value = formatTime(input.value);
        }, 0);
    };

    const handleThumbnailChange = (event) => {
        const file = event.target.files[0];
        if (file && file.type.startsWith('image/')) {
            const reader = new FileReader();
            reader.onload = function (e) {
                setPreviewUrl(e.target.result);
            }
            reader.readAsDataURL(file);
        }
    }

    const [loadingModulos, setLoadingModulos] = useState(true);
    const [dataModulos, setDataModulos] = useState([]);
    useEffect(() => {
        async function fetchData() {
            try {
                setLoadingModulos(true);
                const response = await Api.get(`api/listar-modulos/${id}`, {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                });
                setDataModulos(response.data);
            } catch (error) {
                console.error(error.response?.data?.message || error);
            } finally {
                setLoadingModulos(false);
            }
        }
        fetchData();
    }, [token, id]);

    const [loadingCurso, setLoadingCurso] = useState(true);
    const [dataInfoCurso, setDataInfoCurso] = useState([]);
    useEffect(() => {
        async function fetchData() {
            try {
                setLoadingCurso(true);
                const response = await Api.get(`api/info-curso/${id}`, {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                });
                setDataInfoCurso(response.data);
            } catch (error) {
                console.error(error.response?.data?.message || error);
                navigate("/");
            } finally {
                setLoadingCurso(false);
            }
        }
        fetchData();
    }, [token, id, navigate]);

    // Função para recarregar módulos
    const recarregarModulos = useCallback(async () => {
        try {
            // Não mostrar loading se já tiver módulos carregados (evita "piscar")
            const currentModulosCount = dataModulos.length;
            if (currentModulosCount === 0) {
                setLoadingModulos(true);
            }
            const response = await Api.get(`api/listar-modulos/${id}`, {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });
            setDataModulos(response.data);
        } catch (error) {
            console.error(error.response?.data?.message || error);
        } finally {
            setLoadingModulos(false);
        }
    }, [token, id, dataModulos.length]);

    // Memoizar array de cursos para evitar recriação a cada render
    const cursoId = dataInfoCurso.length > 0 && dataInfoCurso[0] ? dataInfoCurso[0].id : null;
    const cursosArray = useMemo(() => {
        if (cursoId && dataInfoCurso.length > 0 && dataInfoCurso[0]) {
            return [dataInfoCurso[0]];
        }
        return [];
    }, [cursoId, dataInfoCurso]);

    // Inicializar AulasManager com idCurso do params
    // IMPORTANTE: AulasManager contém hooks, então deve ser SEMPRE chamado (não condicionalmente)
    // Passar valores padrão quando cursoId não estiver disponível
    // NÃO pode usar useMemo porque AulasManager contém hooks
    const aulasManager = AulasManager({ 
        token, 
        idCurso: cursoId ? String(id) : "", // Passar string vazia se não tiver cursoId
        dataCursos: cursosArray,
        onModuloCreated: recarregarModulos
    });

    const { register, handleSubmit, control, setValue, formState: { errors } } = useForm();

    const onSubmitEditarModulo = async (data) => {
        setIsLoadingEditarModulo(true);
        const formData = new FormData();

        Object.keys(data).forEach(key => {
            const value = data[key];
            if (typeof value === 'object' && value !== null && !(value instanceof FileList)) {
                formData.append(key, JSON.stringify(value));
            } else if (value instanceof FileList) {
                Array.from(value).forEach(file => {
                    formData.append(key, file);
                });
            } else {
                formData.append(key, value);
            }
        });


        try {
            const response = await Api.post("/api/editar-modulo", formData, {
                headers: {
                    Authorization: `Bearer ${token}`
                },
                onUploadProgress: (progressEvent) => {
                    const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total)
                    setUploadModuloProgress(percentCompleted + "%")
                    if (percentCompleted === 100) {
                        setTimeout(() => {
                            setUploadModuloProgress('Aguarde... finalizando...')
                        }, 1000);
                    }
                }
            });

            setToast({ header: 'Sucesso!', body: response.data.message, type: 'toast-success' })
            setUploadModuloProgress(0)
            setShowModalEditarModulo(false)

        } catch (error) {
            setToast({ header: 'Ops!', body: error.response.data.message, type: 'toast-error' })
            console.error("Erro ao editar modulo:", error.response.data.message);
        } finally {
            setIsLoadingEditarModulo(false);
        }
        setShowToast(true);


    };

    const deletarModulo = async (id) => {
        try {
            const response = await Api.delete(`/api/deletar-modulo/${id}`, {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });
            setToast({ header: 'Sucesso!', body: response.data.message, type: 'toast-success' })
            setShowModalDeletarModulo(false)
            // Recarregar lista de módulos após deletar
            recarregarModulos();
        } catch (error) {
            console.error("Erro ao deletar modulo:", error.response.data.message);
            setToast({ header: 'Ops!', body: error.response.data.message, type: 'toast-error' })
        }
        setShowToast(true)
    }

    // Card em formato de lista para visualização móvel - Design 2025
    // Memoizado para evitar recriação a cada render
    const ModuleListItem = useCallback(({ value }) => {
        return (
            <div key={value.id} className="col-12 mb-3">
                <div className="card border-0 shadow-sm rounded-4 overflow-hidden">
                    <div className="row g-0">
                        <div className="col-12 position-relative">
                            <div style={{ height: '150px' }}>
                                <img
                                    src={`${process.env.REACT_APP_IMG_URL}/${value.thumbnail}`}
                                    className="w-100 h-100"
                                    style={{
                                        objectFit: 'cover',
                                    }}
                                    alt={value.nome}
                                />
                                {/* Overlay escuro para contraste */}
                                <div className="position-absolute w-100 h-100 top-0 start-0"
                                    style={{ background: 'linear-gradient(to bottom, rgba(0,0,0,0) 0%, rgba(0,0,0,0.7) 100%)' }}>
                                </div>

                                {/* Badge de tipo de aula */}
                                <div className="position-absolute top-0 end-0 m-2">
                                    <span className="badge bg-light bg-opacity-75 text-dark rounded-pill px-2 py-1 fs-9">
                                        {value.qtd_aulas} {value.qtd_aulas > 1 ? "aulas" : "aula"}
                                    </span>
                                </div>

                                {/* Título do módulo sobre a imagem */}
                                <div className="position-absolute bottom-0 start-0 p-3 w-100">
                                    <h6 className="card-title fw-semibold text-white mb-0">{value.nome}</h6>
                                </div>
                            </div>
                        </div>
                        <div className="col-12">
                            <div className="card-body px-3 py-2">
                                <div className="d-flex justify-content-between align-items-center">
                                    {nivel === 'Aluno' && (
                                        <div className="d-flex align-items-center flex-grow-1 me-3">
                                            <div className="progress flex-grow-1" style={{ height: '6px', borderRadius: '3px' }}>
                                                <div
                                                    className={`progress-bar ${parseInt(value.porcentagem_concluida) === 100 ? 'bg-success' : 'bg-primary'}`}
                                                    style={{ width: parseInt(value.porcentagem_concluida) + '%' }}
                                                >
                                                </div>
                                            </div>
                                            <span className="ms-2 text-muted fs-8">{parseInt(value.porcentagem_concluida)}%</span>
                                        </div>
                                    )}

                                    <div className="d-flex gap-2 align-items-center justify-content-between w-100">
                                        <Link to={`/aulas/${value.id}`} className="btn btn-primary rounded-3 py-1 px-3">
                                            {nivel === "Aluno" ? "Assistir" : "Acessar"}
                                        </Link>

                                        {nivel === 'Administrador' && (
                                            <div className="btn-group">
                                                <Tippy content="Editar Módulo">
                                                    <button
                                                        onClick={() => {
                                                            setShowModalEditarModulo(true)
                                                            setValue('id', value.id)
                                                            setValue('nome', value.nome)
                                                            setValue('descricao', value.descricao)
                                                            setValue('resumo', value.resumo)
                                                            setValue('carga_horaria', value.carga_horaria)
                                                            setValue('carga_horaria_pratica', value.carga_horaria_pratica || '')
                                                            setValue('thumbnail', value.thumbnail)
                                                            setModuloEditarFile(null)
                                                            setThumbEdit(value.thumbnail)
                                                            setUploadModuloProgress(0)
                                                            setPreviewUrl(null)
                                                        }}
                                                        className="btn btn-sm btn-light"
                                                    >
                                                        <i className="bi bi-pencil"></i>
                                                    </button>
                                                </Tippy>

                                                <Tippy content="Excluir Módulo">
                                                    <button
                                                        onClick={() => { setShowModalDeletarModulo(true); setModuloDeletar({ id: value.id, nome: value.nome }) }}
                                                        className="btn btn-sm btn-light"
                                                    >
                                                        <i className="bi bi-trash"></i>
                                                    </button>
                                                </Tippy>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [nivel, setValue, setShowModalEditarModulo, setModuloEditarFile, setThumbEdit, setUploadModuloProgress, setPreviewUrl, setShowModalDeletarModulo, setModuloDeletar]);

    // Card tradicional para desktop
    // Memoizado para evitar recriação a cada render
    const ModuleGridItem = useCallback(({ value }) => {
        return (
            <div key={value.id} className="col-12 col-sm-12 col-md-12 col-lg-6 col-xl-6 col-xxl-4 mt-4">
                <div className="mb-4">
                    <div className="card text-white">
                        <div className="card border-0">
                            <img src={`${process.env.REACT_APP_IMG_URL}/${value.thumbnail}`} className="card-img img-banner" style={{ height: '20vh' }} alt={value.titulo} />
                            <div className="card-img-overlay gradient border-bottom-radius-0">
                                <span className="fs-5 fw-semibold text-white d-flex">{value.nome}</span>
                                <span className="text-white badge bg-primary">{value.qtd_aulas} {value.qtd_aulas > 1 ? "aulas" : "aula"}</span>
                            </div>
                        </div>
                        <div className="card-body bg-dark">
                            {nivel === 'Aluno' &&
                                <div className="progress w-100 mb-3">
                                    <div className={`progress-bar ${parseInt(value.porcentagem_concluida) === 100 ? 'bg-success' : 'bg-primary'}`} style={{ width: parseInt(value.porcentagem_concluida) + '%' }}>{parseInt(value.porcentagem_concluida)}%</div>
                                </div>
                            }

                            <div className="d-flex justify-content-between">
                                <Link to={`/aulas/${value.id}`} className="btn btn-outline-light link rounded py-1 w-100">
                                    {nivel === "Aluno" ? "Assistir aulas" : "Acessar aulas"}
                                </Link>
                                {nivel === 'Administrador' &&
                                    <div className="d-flex">
                                        <Tippy content="Editar Módulo">
                                            <span onClick={() => {
                                                setShowModalEditarModulo(true)
                                                setValue('id', value.id)
                                                setValue('nome', value.nome)
                                                setValue('descricao', value.descricao)
                                                setValue('resumo', value.resumo)
                                                setValue('carga_horaria', value.carga_horaria)
                                                setValue('carga_horaria_pratica', value.carga_horaria_pratica || '')
                                                setValue('thumbnail', value.thumbnail)
                                                setModuloEditarFile(null)
                                                setThumbEdit(value.thumbnail)
                                                setUploadModuloProgress(0)
                                                setPreviewUrl(null)
                                            }} className="d-flex align-items-center rounded link ms-3 bg-white text-primary py-1 px-2 cursor-pointer">
                                                <i className="bi bi-pencil"></i>
                                            </span>
                                        </Tippy>

                                        <Tippy content="Excluir Módulo">
                                            <span onClick={() => { setShowModalDeletarModulo(true); setModuloDeletar({ id: value.id, nome: value.nome }) }} className="d-flex align-items-center rounded link ms-3 bg-white text-primary py-1 px-2 cursor-pointer">
                                                <i className="bi bi-trash"></i>
                                            </span>
                                        </Tippy>
                                    </div>
                                }
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [nivel, setValue, setShowModalEditarModulo, setModuloEditarFile, setThumbEdit, setUploadModuloProgress, setPreviewUrl, setShowModalDeletarModulo, setModuloDeletar, dataInfoCurso]);


    return (
        <>
            <Toast
                show={showToast}
                onClose={() => setShowToast(false)}
                delay={4000}
                autohide
                className={`position-fixed top-0 end-0 m-3 toast-icon ${toast.type || 'toast-error'}`}
                style={{ zIndex: 1060 }}
            >
                <Toast.Header>
                    <strong className="me-auto">{toast.header}</strong>
                </Toast.Header>
                <Toast.Body>{toast.body}</Toast.Body>
            </Toast>

            <div className="fade-in w-100 mb-5">
                {/* Desktop: Voltar | links de ações (centro) | curso */}
                <div className="d-none d-md-flex align-items-center gap-2 gap-md-3 mb-4 flex-wrap">
                    <button
                        type="button"
                        onClick={() => navigate(-1)}
                        className="btn btn-outline-primary rounded-pill px-4 py-1 d-flex align-items-center gap-2 shadow-sm flex-shrink-0"
                        style={{
                            transition: 'all 0.3s ease',
                            borderWidth: '2px',
                            fontWeight: '500'
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.transform = 'translateX(-5px)';
                            e.currentTarget.style.boxShadow = '0 4px 12px rgba(13, 110, 253, 0.3)';
                            e.currentTarget.style.backgroundColor = '#0d6efd';
                            e.currentTarget.style.color = 'white';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.transform = 'translateX(0)';
                            e.currentTarget.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.1)';
                            e.currentTarget.style.backgroundColor = 'transparent';
                            e.currentTarget.style.color = '#0d6efd';
                        }}
                    >
                        <i className="bi bi-arrow-left fs-5"></i>
                        <span>Voltar</span>
                    </button>

                    {!loadingCurso && dataInfoCurso[0] && (
                        <nav
                            className="flex-grow-1 d-flex flex-wrap justify-content-end align-items-center gap-2 gap-lg-3 px-1 min-w-0"
                            aria-label="Ações do curso"
                        >
                            <button
                                type="button"
                                className="btn btn-link btn-sm text-primary py-1 px-2 text-decoration-none"
                                onClick={() => setShowModalDetalhesCurso(true)}
                            >
                                <i className="bi bi-info-circle me-1"></i>
                                Detalhes
                            </button>
                            {nivel === 'Administrador' && (
                                <button
                                    type="button"
                                    className="btn btn-link btn-sm text-success py-1 px-2 text-decoration-none"
                                    onClick={() => cursoId && aulasManager && aulasManager.openAddModuloModal()}
                                >
                                    <i className="bi bi-plus-circle me-1"></i>
                                    Criar Módulo
                                </button>
                            )}
                            {(nivel === 'Administrador' || nivel === 'Gestor' || nivel === 'RH') && (
                                <Link
                                    to={`/listar-alunos/${id}`}
                                    className="btn btn-link btn-sm text-primary py-1 px-2 text-decoration-none"
                                >
                                    <i className="bi bi-bar-chart-line me-1"></i>
                                    Análise geral dos alunos
                                </Link>
                            )}
                        </nav>
                    )}

                </div>

                {loadingCurso ? (
                    <>
                        {/* Skeletons para banner/header */}
                        <CourseBannerSkeleton />
                        <CourseHeaderMobileSkeleton />
                    </>
                ) : dataInfoCurso[0] && (
                    <>
                        {/* Conteúdo principal */}
                        <div className="row g-0">
                            <div className="col-12">
                                {/* Banner para desktop */}
                                <div 
                                    className="card card-flag rounded-4 w-100 d-none d-md-block overflow-hidden mb-4 border-none position-relative" 
                                    style={{ 
                                        height: '250px',
                                        maxHeight: '250px',
                                        minHeight: '250px'
                                    }}
                                >
                                    <div className="position-absolute w-100 h-100 top-0 start-0" style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)', zIndex: 1 }}></div>
                                    <img 
                                        src={`${process.env.REACT_APP_IMG_URL}/${dataInfoCurso[0].thumbnail}`} 
                                        className="card-img w-100 position-absolute top-0 start-0" 
                                        alt={dataInfoCurso[0].nome}
                                        style={{ 
                                            height: '250px',
                                            objectFit: 'cover',
                                            width: '100%',
                                            zIndex: 0
                                        }}
                                    />
                                    <div className="card-img-overlay d-flex flex-column justify-content-between p-3 position-relative" style={{ zIndex: 2, height: '100%' }}>
                                        <div className="text-white text-center my-auto px-2">
                                            <h5 className="mb-1 fw-semibold" style={{ fontSize: 'clamp(1rem, 2vw, 1.5rem)' }}>{dataInfoCurso[0].nome}</h5>
                                            <p className="mb-0 d-none d-lg-block" style={{ fontSize: 'clamp(0.75rem, 1.5vw, 1rem)' }}>{dataInfoCurso[0].resumo}</p>
                                        </div>
                                        <div className="d-flex info align-items-start justify-content-start mb-2">
                                            <div className="rounded-pill p-1 px-3 opacity fw-semibold">
                                                <span className="text-white">Módulos</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Header moderno para mobile - Estilo 2025 */}
                                <div className="d-md-none mb-3">
                                    <div className="position-relative rounded-4 overflow-hidden mb-3 shadow-sm">
                                        {/* Botão Voltar no Banner Mobile */}
                                        <button 
                                            type="button"
                                            onClick={() => navigate(-1)} 
                                            className="btn btn-light rounded-circle position-absolute top-0 start-0 m-3 d-flex align-items-center justify-content-center shadow"
                                            style={{
                                                width: '40px',
                                                height: '40px',
                                                zIndex: 10,
                                                transition: 'all 0.2s ease'
                                            }}
                                            onTouchStart={(e) => {
                                                e.currentTarget.style.transform = 'scale(0.95)';
                                                e.currentTarget.style.backgroundColor = '#e9ecef';
                                            }}
                                            onTouchEnd={(e) => {
                                                e.currentTarget.style.transform = 'scale(1)';
                                                e.currentTarget.style.backgroundColor = 'white';
                                            }}
                                        >
                                            <i className="bi bi-arrow-left fs-5 text-primary"></i>
                                        </button>
                                        
                                        <img
                                            src={`${process.env.REACT_APP_IMG_URL}/${dataInfoCurso[0].thumbnail}`}
                                            className="w-100"
                                            style={{ height: '220px', objectFit: 'cover' }}
                                            alt={dataInfoCurso[0].nome}
                                        />
                                        <div className="position-absolute w-100 h-100 top-0 start-0"
                                            style={{ background: 'linear-gradient(to bottom, rgba(0,0,0,0.1) 0%, rgba(0,0,0,0.85) 100%)' }}>
                                        </div>
                                        <div className="position-absolute bottom-0 start-0 w-100 p-3 text-white">
                                            <h5 className="fw-bold mb-1 text-white" style={{ fontSize: '1.25rem', lineHeight: '1.3' }}>
                                                {dataInfoCurso[0].nome}
                                            </h5>
                                            {dataInfoCurso[0].resumo && (
                                                <p className="mb-0 text-white-50 small d-none d-sm-block" style={{ fontSize: '0.875rem' }}>
                                                    {dataInfoCurso[0].resumo}
                                                </p>
                                            )}
                                        </div>
                                    </div>

                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                        <div className="d-flex align-items-center">
                                            <div className="badge bg-primary text-white rounded-pill px-3 py-2">
                                                <i className="bi bi-collection me-1"></i>
                                                Módulos ({dataModulos.length})
                                            </div>
                                        </div>

                                        <div className="d-flex gap-2">
                                            <button 
                                                onClick={() => setShowModalDetalhesCurso(true)}
                                                className="btn btn-outline-light rounded-pill px-3"
                                            >
                                                <i className="bi bi-info-circle me-1"></i>
                                                Detalhes
                                            </button>
                                            <button 
                                                onClick={() => setShowDrawerMobile(true)}
                                                className="btn btn-primary rounded-pill px-3"
                                            >
                                                <i className="bi bi-list me-2"></i>Ações
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                <div className="px-0 py-1">
                                    {/* Reciclagem automática: o curso é liberado sozinho perto do vencimento; o certificado anterior vale até vencer */}
                                    {(certificadoStatus?.reciclagem_liberada || certificadoStatus?.dentro_janela_reciclagem) && (nivel === 'Aluno' || nivel === 'Gestor') && (
                                        <Alert variant={certificadoStatus.certificado_expirado ? 'warning' : 'info'} className="mb-3">
                                            <i className={`bi ${certificadoStatus.certificado_expirado ? 'bi-exclamation-triangle-fill' : 'bi-info-circle-fill'} me-2`}></i>
                                            {certificadoStatus.message}
                                        </Alert>
                                    )}
                                </div>

                                {/* Seção de módulos - renderiza sempre, mesmo quando curso está carregando */}
                                <div className="px-0 py-1">
                                    <section className="w-100 row g-2 mx-0" style={{ transition: 'opacity 0.15s ease-in-out' }}>
                                        {loadingModulos ? (
                                            <ModuleSkeletons
                                                count={6}
                                                showProgressBar={nivel === 'Aluno'}
                                                isMobile={isMobile}
                                            />
                                        ) : (
                                            dataModulos.map((value) => (
                                                isMobile ?
                                                    <ModuleListItem key={value.id} value={value} /> :
                                                    <ModuleGridItem key={value.id} value={value} />
                                            ))
                                        )}
                                    </section>
                                </div>
                            </div>
                        </div>
                    </>
                )}
            </div>

            <Modal size="lg" show={showModalEditarModulo} onHide={() => setShowModalEditarModulo(false)} backdrop="static">
                <form onSubmit={handleSubmit(onSubmitEditarModulo)}>
                    <Modal.Header closeButton>
                        <span className="fw-semibold fs-5 text-primary">Editar módulo</span>
                    </Modal.Header>
                    <Modal.Body className="bg-gray-100 p-4">
                        <input type="hidden" {...register('id', { required: true })} />
                        <div className="container">
                            <div className="row">
                                <div className="col-md-12">
                                    <div className="body">
                                        <div className="d-flex mb-5">
                                            <div className="d-flex flex-column w-100">
                                                <div className="row">

                                                    <div className="col-12 col-md-12">
                                                        <div className="mb-3">
                                                            <label className="form-label text-primary fw-medium">Nome</label>
                                                            <input type="text" {...register('nome', { required: true })} placeholder="Nome do curso" className="form-control border-new-light" />
                                                            {errors.nome && <span className='text-danger fs-7'>Nome do curso é obrigatório.</span>}
                                                        </div>
                                                    </div>

                                                    <div className="col-12 col-md-12">
                                                        <div className="mb-3">
                                                            <label className="form-label text-primary fw-medium">Descrição</label>
                                                            <TextareaAutosize {...register('descricao')} placeholder="Breve descrição do curso" className="form-control border-new-light" />
                                                        </div>
                                                    </div>

                                                    <div className="col-12 col-md-4">
                                                        <div className="mb-3">
                                                            <label className="form-label text-primary fw-medium">Thumbnail</label>
                                                            <label htmlFor="thumbnail" className={`form-control label-input ${moduloEditarFile ? 'border-success bg-success' : 'border-primary bg-primary'} text-white fw-medium text-center`}><i className="bi bi-card-image me-2"></i> Trocar Imagem</label>
                                                            <Controller
                                                                control={control}
                                                                name="thumbnail"
                                                                render={({ field: { onChange, onBlur, value, ref } }) => (
                                                                    <input
                                                                        id='thumbnail'
                                                                        type="file"

                                                                        onBlur={onBlur}
                                                                        ref={ref}
                                                                        onChange={(e) => {
                                                                            const files = e.target.files;
                                                                            handleThumbnailChange(e);
                                                                            onChange(files);
                                                                            if (files && files.length > 0) {
                                                                                setModuloEditarFile(true);
                                                                            } else {
                                                                                setModuloEditarFile(false);
                                                                            }
                                                                        }}
                                                                    />
                                                                )}
                                                            />
                                                            {errors.thumbnail && <span className='text-danger fs-7'>Imagem do curso é obrigatório.</span>}
                                                        </div>
                                                    </div>

                                                    <div className="col-12 col-md-4">
                                                        <div className="mb-3">
                                                            <label className="form-label text-primary fw-medium">Carga Horária Total</label>
                                                            <input type="text" maxLength="5" onKeyDown={handleKeyPress} {...register('carga_horaria')} placeholder="00:00" className="form-control border-new-light" />
                                                        </div>
                                                    </div>

                                                    <div className="col-12 col-md-4">
                                                        <div className="mb-3">
                                                            <label className="form-label text-primary fw-medium">Carga Horária Prática</label>
                                                            <input type="text" maxLength="5" onKeyDown={handleKeyPress} {...register('carga_horaria_pratica')} placeholder="00:00" className="form-control border-new-light" />
                                                        </div>
                                                    </div>

                                                    <div className="col-12">
                                                        <div className="mb-3">
                                                            {previewUrl ?
                                                                <img src={previewUrl} className='card-img card-img-edit rounded-3' alt="Thumbnail" />
                                                                :
                                                                <img src={`${process.env.REACT_APP_IMG_URL}/${thumbEdit}`} className='card-img card-img-edit rounded-3' alt="Thumbnail" />
                                                            }
                                                        </div>
                                                    </div>

                                                    <div className="col-12">
                                                        <div className="mb-3">
                                                            <label className="form-label text-primary fw-medium">Resumo</label>
                                                            <TextareaAutosize {...register('resumo')} placeholder="Informações gerais sobre o módulo" className="form-control border-new-light" />
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="progress col-12">
                                                    <div className="progress-bar" role="progressbar" style={{ width: `${uploadModuloProgress}` }} aria-valuenow={uploadModuloProgress} aria-valuemin="0" aria-valuemax="100">{uploadModuloProgress}</div>
                                                </div>

                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </Modal.Body>
                    <Modal.Footer className="justify-content-end">
                        <div className="d-flex justify-content-end">
                            <button type="button" className="btn me-2" onClick={() => setShowModalEditarModulo(false)} disabled={isLoadingEditarModulo}>Cancelar</button>
                            <button type="submit" className="btn btn-primary" disabled={isLoadingEditarModulo}>
                                {isLoadingEditarModulo ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                        Salvando...
                                    </>
                                ) : (
                                    'Salvar'
                                )}
                            </button>
                        </div>
                    </Modal.Footer>
                </form>
            </Modal>

            <Modal size='lg' show={showModalDeletarModulo} onHide={() => setShowModalDeletarModulo(false)}>
                <Modal.Header closeButton>
                    <span className="fw-semibold fs-5 text-primary">Deletar curso</span>
                </Modal.Header>
                <Modal.Body className="bg-gray-100 p-4">
                    <div className="container">
                        <div className="row">
                            <div className="col-md-12">
                                <div className="body">
                                    <div className="d-flex">
                                        <div className="d-flex flex-column w-100">
                                            <div className="row">
                                                <div className="col-12">
                                                    <p>Tem certeza que deseja deletar o módulo <b>{moduloDeletar.nome}</b>?</p>
                                                    <p>Essa ação ira deletar o módulo e todos seus módulos e aulas, e não poderá ser revertida.</p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </Modal.Body>
                <Modal.Footer className="justify-content-end">
                    <div className="d-flex justify-content-end">
                        <button type="button" className="btn me-2" onClick={() => setShowModalDeletarModulo(false)}>Cancelar</button>
                        <button type="button" className="btn btn-danger" onClick={() => deletarModulo(moduloDeletar.id)}>Confirmar</button>
                    </div>
                </Modal.Footer>
            </Modal>

            {/* Modal de Detalhes do Curso */}
            <Modal size="lg" show={showModalDetalhesCurso} onHide={() => setShowModalDetalhesCurso(false)}>
                <Modal.Header closeButton>
                    <span className="fw-semibold fs-5 text-primary">
                        <i className="bi bi-info-circle me-2"></i>
                        Detalhes do Curso
                    </span>
                </Modal.Header>
                <Modal.Body className="bg-gray-100 p-4">
                    {dataInfoCurso[0] && (
                        <div className="container">
                            <div className="row">
                                <div className="col-md-12">
                                    <div className="card border-0 shadow-sm">
                                        <div className="card-body">
                                            <div className="mb-3">
                                                <label className="text-primary fw-medium">Nome do Curso</label>
                                                <p className="mb-0">{dataInfoCurso[0].nome || 'N/A'}</p>
                                            </div>

                                            {dataInfoCurso[0].descricao && (
                                                <div className="mb-3">
                                                    <label className="text-primary fw-medium">Descrição</label>
                                                    <p className="mb-0">{dataInfoCurso[0].descricao}</p>
                                                </div>
                                            )}

                                            {dataInfoCurso[0].resumo && (
                                                <div className="mb-3">
                                                    <label className="text-primary fw-medium">Resumo</label>
                                                    <p className="mb-0">{dataInfoCurso[0].resumo}</p>
                                                </div>
                                            )}

                                            {dataInfoCurso[0].objetivo && (
                                                <div className="mb-3">
                                                    <label className="text-primary fw-medium">Objetivo</label>
                                                    <p className="mb-0" style={{ whiteSpace: 'pre-wrap' }}>{dataInfoCurso[0].objetivo}</p>
                                                </div>
                                            )}

                                            {dataInfoCurso[0].conteudo_programatico && (
                                                <div className="mb-3">
                                                    <label className="text-primary fw-medium">Conteúdo Programático</label>
                                                    <p className="mb-0" style={{ whiteSpace: 'pre-wrap' }}>{dataInfoCurso[0].conteudo_programatico}</p>
                                                </div>
                                            )}

                                            <div className="row">
                                                <div className="col-md-6 mb-3">
                                                    <label className="text-primary fw-medium">Tipo</label>
                                                    <p className="mb-0">
                                                        <span className="badge bg-primary">
                                                            {dataInfoCurso[0].tipo === 'misto' ? 'Híbrido' : 'Online'}
                                                        </span>
                                                    </p>
                                                </div>
                                                {dataInfoCurso[0].prazo_validade && (
                                                    <div className="col-md-6 mb-3">
                                                        <label className="text-primary fw-medium">Prazo de Validade</label>
                                                        <p className="mb-0">{dataInfoCurso[0].prazo_validade} meses</p>
                                                    </div>
                                                )}
                                            </div>

                                            <div className="row">
                                                {dataInfoCurso[0].dataInicio && (
                                                    <div className="col-md-6 mb-3">
                                                        <label className="text-primary fw-medium">Data de Início</label>
                                                        <p className="mb-0">{new Date(dataInfoCurso[0].dataInicio).toLocaleDateString('pt-BR')}</p>
                                                    </div>
                                                )}
                                                {dataInfoCurso[0].dataFinal && (
                                                    <div className="col-md-6 mb-3">
                                                        <label className="text-primary fw-medium">Data Final</label>
                                                        <p className="mb-0">{new Date(dataInfoCurso[0].dataFinal).toLocaleDateString('pt-BR')}</p>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer className="justify-content-end">
                    <button type="button" className="btn btn-primary" onClick={() => setShowModalDetalhesCurso(false)}>
                        Fechar
                    </button>
                </Modal.Footer>
            </Modal>

            {/* Modal de Criar Módulo */}
            {aulasManager && aulasManager.renderModals(cursosArray)}

            {/* Drawer Mobile - Menu de Ações */}
            <Offcanvas 
                show={showDrawerMobile} 
                onHide={() => setShowDrawerMobile(false)} 
                placement="end"
                className="d-md-none"
            >
                <Offcanvas.Header closeButton>
                    <Offcanvas.Title>Menu de Ações</Offcanvas.Title>
                </Offcanvas.Header>
                <Offcanvas.Body className="p-0">
                    <ul className="list-group list-group-flush">
                        <li className="list-group-item">
                            <button 
                                onClick={() => {
                                    setShowModalDetalhesCurso(true);
                                    setShowDrawerMobile(false);
                                }} 
                                className="btn btn-link text-decoration-none p-0 w-100 text-start d-flex align-items-center"
                            >
                                <i className="bi bi-info-circle me-2 text-primary"></i>
                                <span className="text-primary">Detalhes do Curso</span>
                            </button>
                        </li>
                        {nivel === 'Administrador' && (
                            <li className="list-group-item">
                                <button 
                                    onClick={() => {
                                        if (cursoId && aulasManager) {
                                            aulasManager.openAddModuloModal();
                                        }
                                        setShowDrawerMobile(false);
                                    }} 
                                    className="btn btn-link text-decoration-none p-0 w-100 text-start d-flex align-items-center"
                                >
                                    <i className="bi bi-plus-circle me-2 text-primary"></i>
                                    <span className="text-primary">Criar Módulo</span>
                                </button>
                            </li>
                        )}
                        {(nivel === 'Administrador' || nivel === 'Gestor' || nivel === 'RH') && (
                            <li className="list-group-item">
                                <Link 
                                    to={`/listar-alunos/${id}`} 
                                    className="text-decoration-none d-flex align-items-center"
                                    onClick={() => setShowDrawerMobile(false)}
                                >
                                    <i className="bi bi-bar-chart-line me-2 text-primary"></i>
                                    <span className="text-primary">Análise geral dos alunos</span>
                                </Link>
                            </li>
                        )}
                    </ul>
                </Offcanvas.Body>
            </Offcanvas>
        </>
    )
}

export default Modulos;